'use client'

import { ComponentProps, ReactNode } from 'react'
import styled, { css } from 'styled-components'

import {
  timingDefault,
  useLmfAnalyticsOnClick,
  useStableCallback,
} from '@syconium/little-miss-figgy'
import { ResponsiveImage } from '@syconium/little-miss-figgy/dist/components/Image/ResponsiveImage'
import { Skeleton } from '@syconium/little-miss-figgy/dist/components/Skeleton'
import {
  TypeStyleBodyNano,
  TypeStyleBodySmall,
  TypeStyleHeadlineExtraExtraSmall,
} from '@syconium/little-miss-figgy/dist/components/TypeStyle'
import { fromMd, untilMd } from '@syconium/little-miss-figgy/dist/constants/breakpoints'

import { usePageTypeTracking } from '../../_hooks/useStaticTracking'
import {
  resolveSecondaryLocation,
  useTrackingLocation,
} from '../../_providers/TrackingLocationProvider.client'
import { useTrackProductInteraction } from '../../_providers/TrackingProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { NextLink } from '../navigation/NextLink'

import type {
  ProductCatalogAvailability,
  ProductCatalogDefaultShopifyProduct,
  ProductCatalogVariantGroup,
} from '../../../__generated__/graphql/catalog/graphql'

export type ProductTileAspectRatio = 'grid' | 'slider' | 'square' | number

export const productTileAspectRatios: {
  md: Record<Exclude<ProductTileAspectRatio, number>, number>
  sm: Record<Exclude<ProductTileAspectRatio, number>, number>
} = {
  md: {
    square: 1,
    grid: 0.9014,
    slider: 0.7472,
  },
  sm: {
    square: 1,
    grid: 0.9014,
    slider: 0.7435,
  },
}

const ProductTileTitle = styled(TypeStyleHeadlineExtraExtraSmall)<{ $truncate?: number }>`
  overflow: hidden;
  text-overflow: ellipsis;

  ${o =>
    o.$truncate &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: ${o.$truncate};
      -webkit-box-orient: vertical;
      min-height: calc(1.5em * ${o.$truncate}); // base line-height * 2
    `}
`

const ProductTileTitleSkeleton = () => {
  return (
    <ProductTileTitle asTag='div'>
      <Skeleton height='19px' maxWidth='176px' />
    </ProductTileTitle>
  )
}

type ProductTileImageProps = Omit<
  ComponentProps<typeof ResponsiveImage>,
  'aspectRatios' | 'ref'
> & {
  aspectRatio: ProductTileAspectRatio
  /** Whether or not the image should be displayed in a "dimmed" state. */
  dimmed?: boolean
}

const ProductTileResponsiveImage = styled(ResponsiveImage)<{ $dimmed?: boolean }>`
  transition: ${timingDefault} opacity ease-out;
  opacity: ${o => (o.$dimmed ? 0.5 : 1)};
`

const ProductTileImage = ({
  aspectRatio,
  dimmed,
  ...responsiveImageProps
}: ProductTileImageProps) => {
  const aspectRatioSm =
    typeof aspectRatio === 'number' ? aspectRatio : productTileAspectRatios.sm[aspectRatio]
  const aspectRatioMd =
    typeof aspectRatio === 'number' ? aspectRatio : productTileAspectRatios.md[aspectRatio]
  return (
    <ProductTileResponsiveImage
      {...responsiveImageProps}
      $dimmed={dimmed}
      aspectRatios={{
        sm: aspectRatioSm,
        md: aspectRatioMd,
      }}
    />
  )
}

const ProductTileImageOverlayContainer = styled.div`
  position: relative;
`

/** Display the pertinent product image overlay depending on the variant groups availability. */
function ProductTileImageOverlay({
  availability,
  children,
}: {
  availability: ProductCatalogAvailability
  children: ReactNode
}) {
  const {
    pages: { product: productStrings },
  } = useTranslation()

  return (
    <ProductTileImageOverlayContainer>
      {children}
      {availability === 'OUT_OF_STOCK' && (
        <ProductTileOutOfStockOverlay>{productStrings.outOfStock}</ProductTileOutOfStockOverlay>
      )}
    </ProductTileImageOverlayContainer>
  )
}

const ProductTileStyledImageWrap = styled.div<{
  $aspectRatioSm: number
  $aspectRatioMd: number
}>`
  ${untilMd} {
    padding-top: ${o => (1 / o.$aspectRatioSm) * 100}%;
  }
  ${fromMd} {
    padding-top: ${o => (1 / o.$aspectRatioMd) * 100}%;
  }
  position: relative;
`

const ProductTileStyledImageSkeleton = styled(Skeleton)`
  position: absolute;
  top: 0;
`

type ProductTileImageSkeletonProps = {
  aspectRatio: ProductTileAspectRatio
}

const ProductTileImageSkeleton = ({ aspectRatio }: ProductTileImageSkeletonProps) => {
  const aspectRatioSm =
    typeof aspectRatio === 'number' ? aspectRatio : productTileAspectRatios.sm[aspectRatio]
  const aspectRatioMd =
    typeof aspectRatio === 'number' ? aspectRatio : productTileAspectRatios.md[aspectRatio]
  return (
    <ProductTileStyledImageWrap $aspectRatioSm={aspectRatioSm} $aspectRatioMd={aspectRatioMd}>
      <ProductTileStyledImageSkeleton animated />
    </ProductTileStyledImageWrap>
  )
}

const ProductTileColors = styled(TypeStyleHeadlineExtraExtraSmall).attrs(props => {
  return {
    asTag: props.asTag ?? 'div',
    color: props.color ?? 'tertiary',
  }
})`
  margin-top: 8px;
`

const ProductTileColorName = styled.span`
  margin-right: 12px;
`

const ProductTileColorCount = styled.span`
  font-weight: 500;
`

const ProductTileColorsSkeleton = () => {
  return (
    <ProductTileColors>
      <Skeleton height='19px' maxWidth='157px' />
    </ProductTileColors>
  )
}

const ProductTilePriceDisplayWrap = styled(TypeStyleBodySmall)<{
  $noMargin?: boolean
}>`
  font-weight: 700;
  &:not(:first-child) {
    margin-top: ${o => o.theme.spacing(o.$noMargin ? 0 : 2)};
  }
`

type ProductTilePriceProps = {
  children?: React.ReactNode
  /* Disable order-sensitive margin that is applied to the price line. Useful when overriding general spacing of tile detail elements. */
  noMargin?: boolean
}

const ProductTilePrice = ({ children, noMargin }: ProductTilePriceProps) => {
  return (
    <ProductTilePriceDisplayWrap asTag='div' color='primary' $noMargin={noMargin}>
      {children}
    </ProductTilePriceDisplayWrap>
  )
}

const ProductTilePriceSkeleton = () => {
  return (
    <ProductTilePriceDisplayWrap>
      <Skeleton height='19px' maxWidth='107px' />
    </ProductTilePriceDisplayWrap>
  )
}

const ProductTileContentHorizontalLayout = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 16px;
  ${fromMd} {
    gap: 28px;
  }
`

const ProductTileOutOfStockOverlay = styled(TypeStyleBodySmall)`
  position: absolute;
  height: 100%;
  width: 100%;
  background-color: ${o => o.theme.color.fill.background};
  opacity: 0.8;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`

const ProductTileDetails = styled.div<{ untilMdLeftPadding: boolean; noMargin?: boolean }>`
  /* Apply padding to product attributes on small viewports if no gutter is applied */
  padding-left: ${o => (o.untilMdLeftPadding ? o.theme.spacing(2) : 0)};
  ${fromMd} {
    padding-left: 0;
  }

  &:not(:first-child) {
    margin-top: ${o => (o.noMargin ? '0px' : o.theme.spacing(2))};

    ${fromMd} {
      margin-top: ${o => (o.noMargin ? '0px' : o.theme.spacing(4))};
    }
  }
`

/* An arbitrary line of text to display within the product details section. */
const ProductTileDetailLine = styled(TypeStyleBodyNano).attrs({ fontWeight: '600' })`
  color: ${o => o.theme.color.stroke.neutral.text.secondary.on.background};
`

const ProductTileTrackedLink = ({
  variantGroup,
  ...nextLinkProps
}: ComponentProps<typeof NextLink> & {
  variantGroup: Pick<ProductCatalogVariantGroup, 'id' | 'title' | 'urlPath' | 'availability'> & {
    product: Pick<ProductCatalogVariantGroup['product'], 'handle'>
    defaultShopifyProduct: Pick<ProductCatalogDefaultShopifyProduct, 'id'>
    color?: null | Pick<NonNullable<ProductCatalogVariantGroup['color']>, 'id'>
  }
  onClick?: Parameters<typeof useLmfAnalyticsOnClick>['0']['onClick']
}) => {
  const { trackProductInteraction } = useTrackProductInteraction()
  const pageType = usePageTypeTracking()
  const trackingLocation = useTrackingLocation()
  const onClick = useStableCallback(() => {
    trackProductInteraction({
      handle: variantGroup.product.handle,
      color: variantGroup.color?.id,
      id: variantGroup.id,
      shopifyId: variantGroup.defaultShopifyProduct.id,
      soldOut: variantGroup.availability === 'OUT_OF_STOCK',
      tracking: {
        location: pageType,
        secondary_location: resolveSecondaryLocation(trackingLocation),
        section_number: trackingLocation.sectionNumber ?? null,
        position: trackingLocation.position ?? null,
        href: typeof nextLinkProps.href === 'string' ? nextLinkProps.href : variantGroup.urlPath,
      },
    })
    nextLinkProps.onClick?.()
  })

  const onClickWithTracking = useLmfAnalyticsOnClick({
    element: 'tile',
    variations: ['product'],
    label: variantGroup.title,
    href: variantGroup.urlPath,
    onClick,
  })

  return <NextLink {...nextLinkProps} onClick={onClickWithTracking} />
}

export {
  ProductTileTrackedLink,
  ProductTileDetails,
  ProductTileDetailLine,
  ProductTileTitle,
  ProductTileTitleSkeleton,
  ProductTileImage,
  ProductTileImageOverlay,
  ProductTileImageSkeleton,
  ProductTileColors,
  ProductTileColorsSkeleton,
  ProductTilePrice,
  ProductTileColorName,
  ProductTileColorCount,
  ProductTilePriceSkeleton,
  ProductTileContentHorizontalLayout,
  ProductTileOutOfStockOverlay,
}
